import { DialogTitle } from "@radix-ui/react-dialog";
import { DialogHeader } from "../../utils/components/ui/dialog";
import { Button } from "../../utils/components/ui/button";
import { ErrorWraper } from "../common/ErrorWrapper";
import { useAddBrand, useEditBrand } from "../../service/brandService";
import {
  AddBrandSchema,
  AddBrandSchemaT,
} from "../../types/schema/brandSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import useBrandRequestStore from "../../store/useBrandStore";
import { useQueryClient } from "@tanstack/react-query";
import useGlobalStore from "../../store/useGlobalStore";
import ImageUploader from "../ImageUpload";
import { toast } from "react-toastify";

interface Props {
  setOpen: (open: boolean) => void;
}

function AddNewBrand({ setOpen }: Props) {
  const queryClient = useQueryClient();
  const { cloudImageUrl } = useGlobalStore();
  const { mutate: addNewBrand } = useAddBrand();
  const { currentBrand, setCurrentBrand } = useBrandRequestStore();
  const { mutate: updateBrand } = useEditBrand();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AddBrandSchemaT>({
    resolver: zodResolver(AddBrandSchema),
    defaultValues: {
      brandName: currentBrand?.brandName || "",
      description: currentBrand?.description || "",
      imageUrl: currentBrand?.imageUrl || "",
      keyword: currentBrand?.keyword || "",
      origin: currentBrand?.origin || "",
      status: currentBrand?.status,
    },
  });

  const handleAddBrand = (data: AddBrandSchemaT) => {
    const postData = currentBrand?.id
      ? {
          ...data,
          id: currentBrand?.id,
          imageUrl: cloudImageUrl || null,
        }
      : {
          ...data,
          brandName: data.brandName,
          description: data.description,
          imageUrl: cloudImageUrl,
          keyword: data.keyword,
          origin: data.origin,
          status: data.status,
        };
    if (currentBrand?.id) {
      updateBrand(postData as unknown, {
        onSuccess: (data) => {
          console.error("Brand Name updated successfully", data);
          setCurrentBrand(null);
          setOpen(false);
          queryClient.invalidateQueries({ queryKey: ["useGetAllBrand"] });
          toast.success("Brand Name Updated successfully!");
        },
        onError: (error: Error) => {
          toast.error(`Error updating Brand: ${error.message}`);
          console.error("Error updating brandName", error);
        },
      });
      return;
    }

    addNewBrand(postData as unknown, {
      onSuccess: (data) => {
        toast.success("Brand Added successfully!");
        queryClient.invalidateQueries({
          queryKey: ["useGetAllBrand"],
        });
        console.log("Brand added:", data);
        setOpen(false);
      },
      onError: (error: Error) => {
        toast.error(`Error adding Brand: ${error.message}`);
        console.error("Error adding brandName", error);
      },
    });
  };

  const handleCancel = () => {
    setOpen(false);
    setCurrentBrand(null);
  };

  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle className="text-[#1A5EF9] text-xl font-semibold">
          Add New Brand
        </DialogTitle>
      </DialogHeader>

      <form onSubmit={handleSubmit(handleAddBrand)} className="space-y-4">
        <div className="flex gap-4">
          {/* Image Upload Section */}
          <div className="flex flex-col items-center justify-center h-60 w-60 overflow-clip rounded border border-[#1A5EF9] bg-[#F3F4F3] cursor-pointer hover:bg-[#e8eae8] transition-colors relative">
            {!currentBrand?.imageUrl && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 text-[#1A5EF9] mb-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            )}
            {currentBrand?.imageUrl ? (
              <>
                <img
                  src={currentBrand.imageUrl}
                  className="h-1/3 w-3/4 object-cover rounded"
                  alt="brand"
                />
                <button
                  type="button"
                  className="absolute top-2 right-2 bg-white rounded-full p-1 shadow hover:bg-red-100"
                  onClick={() =>
                    setCurrentBrand({ ...currentBrand, imageUrl: "" })
                  }
                  title="Remove image"
                  aria-label="Remove image"
                >
                  <span className="text-lg text-red-500 font-bold">×</span>
                </button>
              </>
            ) : (
              <ImageUploader />
            )}
          </div>

          {/* Form Fields Section */}
          <div className="flex-1 space-y-3">
            <div className="grid grid-cols-1 gap-3">
              <label
                className="text-[#1A5EF9] text-sm font-medium mb-1"
                htmlFor="brandName"
              >
                Brand Name
              </label>
              <ErrorWraper message={errors.brandName?.message}>
                <input
                  id="brandName"
                  type="text"
                  className="w-full bg-[#F3F4F3] rounded border border-[#BAC4C0] h-10 px-3 focus:outline-none focus:ring-1 focus:ring-[#1A5EF9]"
                  placeholder="Brand name"
                  {...register("brandName")}
                />
              </ErrorWraper>

              <label
                className="text-[#1A5EF9] text-sm font-medium mb-1"
                htmlFor="origin"
              >
                Origin
              </label>
              <ErrorWraper message={errors.origin?.message}>
                <input
                  id="origin"
                  type="text"
                  className="w-full bg-[#F3F4F3] rounded border border-[#BAC4C0] h-10 px-3 focus:outline-none focus:ring-1 focus:ring-[#1A5EF9]"
                  placeholder="Origin"
                  {...register("origin")}
                />
              </ErrorWraper>

              <label
                className="text-[#1A5EF9] text-sm font-medium mb-1"
                htmlFor="keyword"
              >
                Keyword
              </label>
              <ErrorWraper message={errors.keyword?.message}>
                <input
                  id="keyword"
                  type="text"
                  className="w-full bg-[#F3F4F3] rounded border border-[#BAC4C0] h-10 px-3 focus:outline-none focus:ring-1 focus:ring-[#1A5EF9]"
                  placeholder="Keyword"
                  {...register("keyword")}
                />
              </ErrorWraper>

              <label
                className="text-[#1A5EF9] text-sm font-medium mb-1"
                htmlFor="description"
              >
                Brand Description
              </label>
              <ErrorWraper message={errors.description?.message}>
                <textarea
                  id="description"
                  className="w-full bg-[#F3F4F3] rounded border border-[#BAC4C0] p-3 h-24 focus:outline-none focus:ring-1 focus:ring-[#1A5EF9]"
                  placeholder="Brand description"
                  {...register("description")}
                />
              </ErrorWraper>
            </div>
          </div>
        </div>

        {/* Footer Section */}
        <div className="flex justify-between items-center pt-4">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 text-[#1A5EF9] rounded border-gray-300 focus:ring-[#1A5EF9]"
              defaultChecked={currentBrand?.status !== false}
              {...register("status")}
            />
            <span className="ml-2 text-[#1A5EF9] text-sm font-medium">
              Status: Active
            </span>
          </label>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              className="border-[#BAC4C0] text-[#84928C] hover:bg-gray-50 px-6 py-2"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#1A5EF9] hover:bg-[#7cb01d] text-white px-6 py-2"
            >
              Save Brand
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default AddNewBrand;
